/**
 * PERMISSIONS_CONFIG
 *
 * Centralized permission configuration for the IC Transform RBAC system.
 * Defines 21 permission keys across 3 role tiers with hierarchical inheritance:
 *   admin ⊃ operator ⊃ viewer
 *
 * Permission keys are dot-namespaced strings: "<domain>.<action>"
 * The same keys are enforced by the backend.
 *
 * Report Builder RBAC:
 *   Two preset categories — the MOR preset (Monthly Operational Report) and
 *   all OTHER presets in Report Builder. Each category has three perms:
 *     view          — read-only access to generated/saved snapshots
 *     generate      — run/pre-generate snapshots and edit free-text fields
 *     template.edit — modify the preset's widgets, layout, sources
 *
 *   MOR perms (report_builder.mor.*) are NOT in any role tier.
 *   They are granted exclusively when the user is in TRANSFORM_TEAM,
 *   handled directly in AuthContext.deriveUser. No {CLIENT}_ADMIN or
 *   FULL_OPERATOR user ever receives MOR visibility.
 *
 *   Other preset perms follow the normal tier hierarchy:
 *     viewer    → view
 *     operator  → view + generate
 *     admin     → view + generate + template.edit
 *
 * Usage:
 *   import { getPermissionsForRole, PERMISSIONS } from '../utils/permissionsConfig';
 *   const perms = getPermissionsForRole('operator');
 *   perms.has(PERMISSIONS.REPORTS_EXPORT); // true
 */

// ─── Permission Key Constants ──────────────────────────────────────────────────

/**
 * All permission keys in the system.
 * Frozen — do not add keys here without updating the backend permission matrix.
 *
 * @type {Readonly<Record<string, string>>}
 */
export const PERMISSIONS = Object.freeze({
  /** View the main client dashboard */
  DASHBOARD_VIEW: "dashboard.view",

  /** Trigger a health check refresh */
  HEALTH_REFRESH: "health.refresh",

  /** View validation results */
  VALIDATIONS_VIEW: "validations.view",

  /** Trigger a validation run */
  VALIDATIONS_TRIGGER: "validations.trigger",

  /** View ETL job status */
  ETL_JOBS_VIEW: "etl.jobs.view",

  /** View reports */
  REPORTS_VIEW: "reports.view",

  /** View the performance/trend reports */
  REPORTS_PERFORMANCE_VIEW: "reports.performance.view",

  /** Generate (re-run) a report */
  REPORTS_GENERATE: "reports.generate",

  /** Export a report to Excel/PDF */
  REPORTS_EXPORT: "reports.export",

  /** Configure IC Police report settings | NOT PRESENT in application, reserved for future use */
  REPORTS_ICPOLICE_CONFIGURE: "reports.icpolice.configure",

  /** View incidents */
  INCIDENTS_VIEW: "incidents.view",

  /** Create or update incidents | NOT PRESENT in application, reserved for future use*/
  INCIDENTS_CREATE: "incidents.create",

  /** View message/work queues */
  QUEUES_VIEW: "queues.view",

  /** Manage (run/requeue) work queue operations */
  QUEUES_MANAGE: "queues.manage",

  /** Edit application settings | NOT PRESENT in application, reserved for future use */
  SETTINGS_EDIT: "settings.edit",

  // ─── Report Builder ─ MOR preset (Monthly Operational Report) ────────────────────
  /** View MOR preset — list, open snapshots, read-only PDF view */
  REPORT_BUILDER_MOR_VIEW: "report_builder.mor.view",
  /** Run/pre-generate the MOR preset and edit free-text/commentary widgets */
  REPORT_BUILDER_MOR_GENERATE: "report_builder.mor.generate",
  /** Edit MOR preset template (widgets, layout, sources) — admin or MOR_TEMPLATE_EDITOR */
  REPORT_BUILDER_MOR_TEMPLATE_EDIT: "report_builder.mor.template.edit",

  // ─── Report Builder ─ Other presets (everything except MOR) ───────────────────
  /** View any non-MOR preset — list, open snapshots, read-only PDF view */
  REPORT_BUILDER_PRESET_VIEW: "report_builder.preset.view",
  /** Run/pre-generate non-MOR presets and edit free-text/commentary widgets */
  REPORT_BUILDER_PRESET_GENERATE: "report_builder.preset.generate",
  /** Edit non-MOR preset templates (widgets, layout, sources) — admin or PRESET_TEMPLATE_EDITOR */
  REPORT_BUILDER_PRESET_TEMPLATE_EDIT: "report_builder.preset.template.edit",
});

// ─── Role Tier Definitions ─────────────────────────────────────────────────────

/**
 * viewer — read-only access across all features.
 * 8 permissions.
 */
const VIEWER_PERMISSIONS = [
  PERMISSIONS.DASHBOARD_VIEW,
  PERMISSIONS.VALIDATIONS_VIEW,
  PERMISSIONS.ETL_JOBS_VIEW,
  PERMISSIONS.REPORTS_VIEW,
  PERMISSIONS.REPORTS_PERFORMANCE_VIEW,
  PERMISSIONS.INCIDENTS_VIEW,
  PERMISSIONS.QUEUES_VIEW,
  PERMISSIONS.REPORT_BUILDER_PRESET_VIEW,
];

/**
 * operator — viewer permissions + interactive operations.
 * 14 permissions (viewer + 6).
 */
const OPERATOR_PERMISSIONS = [
  ...VIEWER_PERMISSIONS,
  PERMISSIONS.HEALTH_REFRESH,
  PERMISSIONS.VALIDATIONS_TRIGGER,
  PERMISSIONS.REPORTS_GENERATE,
  PERMISSIONS.REPORTS_EXPORT,
  PERMISSIONS.QUEUES_MANAGE,
  PERMISSIONS.REPORT_BUILDER_PRESET_GENERATE,
];

/**
 * admin — full access. Superset of operator.
 * 18 permissions (operator + 4).
 */
const ADMIN_PERMISSIONS = [
  ...OPERATOR_PERMISSIONS,
  PERMISSIONS.REPORTS_ICPOLICE_CONFIGURE,
  PERMISSIONS.INCIDENTS_CREATE,
  PERMISSIONS.SETTINGS_EDIT,
  PERMISSIONS.REPORT_BUILDER_PRESET_TEMPLATE_EDIT,
];

// Note: report_builder.mor.* keys exist in PERMISSIONS but are intentionally
// absent from all role tier arrays. They are injected only for TRANSFORM_TEAM
// members in AuthContext.deriveUser.

// ─── Exported Config ───────────────────────────────────────────────────────────

/**
 * PERMISSIONS_CONFIG
 *
 * Maps each role tier to its frozen array of permission keys.
 * Hierarchy: admin ⊃ operator ⊃ viewer
 *
 * @type {Readonly<{ viewer: readonly string[], operator: readonly string[], admin: readonly string[] }>}
 */
export const PERMISSIONS_CONFIG = Object.freeze({
  viewer: Object.freeze([...VIEWER_PERMISSIONS]),
  operator: Object.freeze([...OPERATOR_PERMISSIONS]),
  admin: Object.freeze([...ADMIN_PERMISSIONS]),
});

// ─── Utility ───────────────────────────────────────────────────────────────────

/**
 * Returns the set of permission keys for a given role tier.
 *
 * Hierarchy: admin ⊃ operator ⊃ viewer
 *
 * @param {string} role - One of "viewer" | "operator" | "admin"
 * @returns {Set<string>} Set of permission key strings, or empty Set for unknown roles
 *
 * @example
 * getPermissionsForRole("operator").has("reports.export"); // true
 * getPermissionsForRole("viewer").has("queues.manage");    // false
 * getPermissionsForRole("invalid");                        // Set {}
 */
export function getPermissionsForRole(role) {
  const perms = PERMISSIONS_CONFIG[role];
  if (!perms) return new Set();
  return new Set(perms);
}
